import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import iziToast from "izitoast";
import { saveDistributorConfigAPI } from "actions/bomItemActions";
import { getActiveDistributorKeys, testDistributorConnectionAPI } from "actions/distributorActions";
import { saveBillingAPI } from "actions/companyActions";
import {
  Mail,
  Building2,
  MapPin,
  FileText,
  KeyRound,
  AlertCircle,
  Check,
  ArrowRight,
  ArrowLeft,
  Plus,
  Activity,
} from "lucide-react";
import Semi360Logo from "components/Semi360Logo";
import "./LoginPage.css";

// Step-rail circle state: done = teal, active = violet, pending = white w/ border.
const stepCircle = (n, step) => {
  if (step > n) return "bg-s360-teal text-s360-ink";
  if (step === n) return "bg-s360-violet text-white";
  return "bg-white border border-s360-stroke2 text-s360-fg3";
};

const Glyph = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 200 200"
    xmlns="http://www.w3.org/2000/svg"
    fill="#24D0C0"
    aria-hidden="true"
  >
    <path d="M100 0C44.7 0 0 44.7 0 100s44.7 100 100 100 100-44.7 100-100S155.3 0 100 0Zm0 134.6c-19.1 0-34.6-15.6-34.6-34.6S80.9 65.4 100 65.4s34.6 15.6 34.6 34.6-15.5 34.6-34.6 34.6Zm53.3-34.6c0-29.5-23.9-53.4-53.3-53.4-19.6 0-36.8 10.7-46 26.5 5.6-27.8 30.2-48.7 59.5-48.7 33.5 0 60.7 27.3 60.7 60.9 0 27.4-18.1 50.6-43 58.2 13.4-9.7 22.1-25.5 22.1-43.5Zm-53.3 81.7c-44.7 0-81.1-36.5-81.1-81.7 0-27.2 13.4-51.3 33.8-66.1-11.6 13.9-18.6 31.7-18.6 51.2 0 44.1 35.5 79.7 79.4 79.7 22.2 0 42.4-9.2 56.7-23.9-14.1 24.2-40.3 40.8-70.2 40.8Z" />
  </svg>
);

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const COUNTRIES = ["India", "United States", "United Kingdom", "Germany", "Singapore", "United Arab Emirates", "Australia"];
const ENV_OPTS = ["Production", "Sandbox"];
const STATUS_FIELD = { k: "status", label: "Status", type: "toggle", options: ["Enabled", "Disabled"] };
const envField = (k, label) => ({ k, label, type: "select", options: ENV_OPTS });

// The API stores status and environment as codes (1/2) but this form works in
// labels, so translate in both directions when loading and saving.
const statusToLabel = (value) => (String(value) === "2" ? "Disabled" : "Enabled");
const statusToCode = (label) => (label === "Disabled" ? 2 : 1);
const envToLabel = (value, options) => {
  const asString = String(value);
  if (asString === "1") return options[0];
  if (asString === "2") return options[1];
  // Already a label (rows previously saved from this screen).
  return options.includes(asString) ? asString : options[0];
};
const envToCode = (label, options) => (label === options[1] ? 2 : 1);

// Secrets come back from the API masked with asterisks (see maskSecretToken).
// Such a value must never be written back, or it would replace the real key.
const isMaskedSecret = (value) => typeof value === "string" && value.includes("*");

// Row columns that live beside the decoded api_keys and must not be treated as
// credentials to carry forward.
const NON_KEY_COLUMNS = ["id", "company_id", "type", "status", "created_at", "updated_at"];

// The API replaces the whole api_keys blob on save (DistributorsController@edit
// does `api_keys = formatApiDataArray($request)`), so anything this screen does
// not render would be dropped - notably Digikey's OAuth access/refresh tokens,
// which are stored in api_keys but only ever set by the OAuth callback.
// Collect those untouched values so they can be sent back unchanged.
const collectUnmanagedKeys = (dist, saved) => {
  const managed = new Set(dist.fields.map((f) => f.k));
  const extra = {};
  Object.keys(saved || {}).forEach((k) => {
    if (managed.has(k) || NON_KEY_COLUMNS.includes(k)) return;
    const value = saved[k];
    if (value === null || value === undefined || value === "") return;
    // A masked secret must not be written back either.
    if (isMaskedSecret(value)) return;
    extra[k] = value;
  });
  return extra;
};

const DISTRIBUTORS = [
  { id: "mouser", name: "Mouser", short: "Mou", color: "#003478", blurb: "API key + order key",
    fields: [STATUS_FIELD, { k: "mouser_api_key", label: "Mouser API key", ph: "Enter the Mouser API key", secret: true }, { k: "mouser_order_api_key", label: "Mouser Order API key", ph: "Enter the Mouser order API key", secret: true }, envField("mouser_order_environment", "Order environment")] },
  { id: "digikey", name: "Digikey", short: "DK", color: "#CC0000", blurb: "OAuth client ID + secret",
    fields: [STATUS_FIELD, { k: "digikey_client_id", label: "Digikey Client ID", ph: "Enter the Digikey client ID" }, { k: "digikey_secret", label: "Digikey Secret", ph: "Enter the Digikey secret", secret: true }, envField("digikey_environment", "Environment")] },
  // TTI and Arrow are email-fulfilled too (neither has an order API), so both
  // carry an order email alongside their search credentials. Optional, like
  // TME/Rutronik's: not part of the credential check, only needed to order.
  { id: "tti", name: "TTI", short: "TTI", color: "#E2231A", blurb: "API key + account number",
    fields: [STATUS_FIELD, { k: "tti_api_key", label: "TTI API key", ph: "Enter the TTI API key", secret: true }, { k: "tti_customer_account_number", label: "TTI Customer account number", ph: "Enter the TTI customer account number" }, { k: "tti_order_email", label: "TTI Order Email", ph: "Enter the TTI order email", optional: true, email: true }] },
  { id: "arrow", name: "Arrow", short: "Arr", color: "#1B1B1B", blurb: "API key + username",
    fields: [STATUS_FIELD, { k: "arrow_api_key", label: "Arrow API key", ph: "Enter the Arrow API key", secret: true }, { k: "arrow_api_username", label: "Arrow API username", ph: "Enter the Arrow API username" }, { k: "arrow_order_email", label: "Arrow Order Email", ph: "Enter the Arrow order email", optional: true, email: true }] },
  { id: "element14", name: "Element14", short: "E14", color: "#FF6900", blurb: "Search key + token + trade account",
    fields: [STATUS_FIELD, { k: "element14_api_key", label: "Search Product API key", ph: "Enter the Search Product API key", secret: true }, { k: "element14_generate_token_api_key", label: "Generate Auth Token API key", ph: "Enter the generate token API key", secret: true }, { k: "element14_trade_account", label: "Element14 Trade account", ph: "Enter Element14 trade account" }] },
  // TME and Rutronik are email-fulfilled (neither has an order API), so both
  // carry an order email alongside their search credentials. It is optional
  // here: it is not part of the credential check and is only needed to place
  // an order, so leaving it blank must not block Test connection or Save.
  { id: "tme", name: "TME", short: "TME", color: "#0A4EA2", blurb: "Token + secret + order email",
    fields: [STATUS_FIELD, { k: "tme_token", label: "TME Token", ph: "Enter the TME token", secret: true }, { k: "tme_secret", label: "TME Secret", ph: "Enter the TME secret", secret: true }, { k: "tme_order_email", label: "TME Order Email", ph: "Enter the TME order email", optional: true, email: true }] },
  { id: "rutronik", name: "Rutronik", short: "Rut", color: "#00509E", blurb: "API key + order email",
    fields: [STATUS_FIELD, { k: "rutronik_api_key", label: "Rutronik API key", ph: "Enter the Rutronik API key", secret: true }, { k: "rutronik_order_email", label: "Rutronik Order Email", ph: "Enter the Rutronik order email", optional: true, email: true }] },
  { id: "avnet", name: "Avnet", short: "Avn", color: "#5F259F", blurb: "API key + order email (WC tokens optional)",
    fields: [STATUS_FIELD, { k: "avnet_api_key", label: "Avnet API key", ph: "Enter the Avnet API key", secret: true }, { k: "avnet_wc_token", label: "Avnet WC token", ph: "Enter the Avnet WCToken, if issued", secret: true, optional: true }, { k: "avnet_wc_trusted_token", label: "Avnet WC trusted token", ph: "Enter the Avnet WCTrustedToken, if issued", secret: true, optional: true }, { k: "avnet_order_email", label: "Avnet Order Email", ph: "Enter the Avnet order email", optional: true, email: true }] },
  // TI reads public ti.com endpoints - there is no API key to enter, so enabling
  // the row is the whole setup.
  { id: "ti", name: "TI", short: "TI", color: "#C8102E", blurb: "No API key required",
    fields: [STATUS_FIELD] },
];

const Field = ({ label, optional, error, children }) => (
  <div className="sb-field">
    <label>
      {label}
      {optional && <span className="text-s360-fg3 font-normal ml-1.5">· optional</span>}
    </label>
    {children}
    {error && (
      <span className="sb-error-text">
        <AlertCircle className="w-3.5 h-3.5" /> {error}
      </span>
    )}
  </div>
);

const OnboardingPage = () => {
  const navigate = useNavigate();
  const user = useSelector((state) => state?.user?.user);
  const email = user?.email || "you@company.com";

  const [step, setStep] = useState(1);

  // Step 1: billing
  const [billing, setBilling] = useState({
    email: user?.email || "",
    bizName: user?.company_name || "",
    line1: "",
    line2: "",
    city: "",
    state: "",
    postal: "",
    country: "India",
    gstin: "",
  });
  const [bTouched, setBTouched] = useState({});
  const setB = (k, v) => setBilling((p) => ({ ...p, [k]: v }));
  const markB = (k) => setBTouched((p) => ({ ...p, [k]: true }));

  const billingErr = (k) => {
    if (!bTouched[k]) return "";
    const v = (billing[k] || "").trim();
    if (k === "email") {
      if (!v) return "Billing email is required.";
      if (!EMAIL_RE.test(v)) return "That doesn't look like a valid email.";
    }
    if (k === "bizName" && !v) return "Registered business name is required.";
    if (k === "line1" && !v) return "Address is required.";
    if (k === "city" && !v) return "City is required.";
    if (k === "state" && !v) return "State is required.";
    if (k === "postal" && !v) return "Postal code is required.";
    return "";
  };
  const billingRequired = ["email", "bizName", "line1", "city", "state", "postal", "country"];
  const billingValid = billingRequired.every((k) => {
    const v = (billing[k] || "").trim();
    if (!v) return false;
    if (k === "email") return EMAIL_RE.test(v);
    return true;
  });

  const [savingBilling, setSavingBilling] = useState(false);

  const continueToStep2 = async () => {
    const all = {};
    billingRequired.forEach((k) => (all[k] = true));
    setBTouched((p) => ({ ...p, ...all }));
    if (!billingValid || savingBilling) return;

    // Persist the billing details before advancing. Field names map to the
    // tri_company billing columns the API expects.
    const payload = {
      billing_email: billing.email.trim(),
      registered_business_name: billing.bizName.trim(),
      billing_line1: billing.line1.trim(),
      billing_line2: billing.line2.trim(),
      billing_city: billing.city.trim(),
      billing_state: billing.state.trim(),
      billing_postal_code: billing.postal.trim(),
      billing_country: billing.country,
      gstin: billing.gstin.trim(),
    };

    setSavingBilling(true);
    try {
      const res = await saveBillingAPI(payload);
      if (res?.status) {
        setStep(2);
      } else {
        iziToast.error({
          title: "Error",
          message: res?.message || "Couldn't save billing details. Please try again.",
          position: "bottomCenter",
        });
      }
    } catch (e) {
      iziToast.error({
        title: "Error",
        message: "Something went wrong, please try again later.",
        position: "bottomCenter",
      });
    } finally {
      setSavingBilling(false);
    }
  };

  // Step 2: distributor keys
  const [connected, setConnected] = useState({});
  const [openId, setOpenId] = useState(null);
  const [draft, setDraft] = useState({});
  const [draftTouched, setDraftTouched] = useState(false);
  const [testStatus, setTestStatus] = useState("idle");
  const [testMessage, setTestMessage] = useState("");
  const [saving, setSaving] = useState(false);

  // Mark that onboarding is in progress so the app can prevent navigation away
  // from this flow until the user explicitly finishes onboarding.
  // We intentionally do NOT remove this on unmount here — it is cleared when
  // the user finishes onboarding (Enter dashboard) or logs out.
  useEffect(() => {
    try {
      sessionStorage.setItem("in_onboarding", "1");
    } catch (e) {
      /* ignore */
    }
  }, []);

  // Pre-load distributors this company has already connected so they show as
  // connected here (and "Enter dashboard" stays enabled after a reload), and so
  // that "Edit" opens with the saved values instead of a blank form.
  useEffect(() => {
    let ignore = false;
    (async () => {
      const res = await getActiveDistributorKeys();
      if (ignore || !res?.status || !res?.data) return;
      const next = {};
      Object.keys(res.data).forEach((type) => {
        const id = String(type).toLowerCase();
        const dist = DISTRIBUTORS.find((d) => d.id === id);
        if (!dist) return;

        const saved = res.data[type] || {};
        const values = {};
        dist.fields.forEach((f) => {
          if (f.k === "status") {
            values.status = statusToLabel(saved.status);
            return;
          }
          const raw = saved[f.k];
          if (raw === null || raw === undefined || raw === "") return;
          if (f.options) {
            // Environment codes (1/2) are stored numerically; the select works
            // in labels.
            values[f.k] = envToLabel(raw, f.options);
            return;
          }
          // The API masks some secrets (Mouser) before returning them. Loading a
          // masked value into the form would write the asterisks back over the
          // real key on save, so leave those blank for the user to re-enter.
          if (isMaskedSecret(raw)) return;
          values[f.k] = String(raw);
        });

        const primary =
          dist.fields.find((f) => f.secret) || dist.fields.find((f) => !f.type);
        const primaryValue = primary ? values[primary.k] : "";
        next[id] = {
          values,
          extra: collectUnmanagedKeys(dist, saved),
          masked: !primary
            ? "No API key required"
            : primaryValue && primaryValue.length > 4
              ? `••••••••${primaryValue.slice(-4)}`
              : "••••••••",
        };
      });
      if (Object.keys(next).length) setConnected((p) => ({ ...next, ...p }));
    })();
    return () => {
      ignore = true;
    };
  }, []);

  const activeDist = DISTRIBUTORS.find((d) => d.id === openId);

  // A field blocks the form when it is required and empty, or when it carries a
  // value in the wrong format. Optional fields (e.g. the order email) only need
  // to be well-formed if the user actually filled them in.
  const fieldError = (f) => {
    const value = (draft[f.k] || "").trim();
    if (!value) return f.optional ? "" : `${f.label} is required.`;
    if (f.email && !EMAIL_RE.test(value)) return "Enter a valid email address.";
    return "";
  };

  const draftValid = activeDist
    ? activeDist.fields.filter((f) => !f.type).every((f) => !fieldError(f))
    : false;

  const editDraft = (k, v) => {
    setDraft((p) => ({ ...p, [k]: v }));
    if (testStatus !== "idle") setTestStatus("idle");
    if (testMessage) setTestMessage("");
  };

  // Validate the typed keys live against the distributor (without saving).
  const testConnection = async () => {
    if (!draftValid || !activeDist) return setDraftTouched(true);
    setTestStatus("testing");
    setTestMessage("");
    try {
      const res = await testDistributorConnectionAPI({ ...draft, type: activeDist.name });
      const ok = !!(res?.status && res?.data?.valid);
      setTestStatus(ok ? "ok" : "fail");
      setTestMessage(res?.data?.message || res?.message || "");
    } catch (e) {
      setTestStatus("fail");
      setTestMessage("Couldn't reach the distributor. Please try again.");
    }
  };

  const openConnect = (id) => {
    const dist = DISTRIBUTORS.find((d) => d.id === id);
    const existing = connected[id]?.values;
    const init = existing ? { ...existing } : {};
    // Default any select/toggle the saved values didn't cover, so the control
    // always has a value whether this is a fresh connect or an edit.
    dist.fields.forEach((f) => { if (f.options && !init[f.k]) init[f.k] = f.options[0]; });
    setDraft(init);
    setOpenId(id);
    setDraftTouched(false);
    setTestStatus("idle");
    setTestMessage("");
  };
  const saveConnect = async () => {
    setDraftTouched(true);
    if (!draftValid || !activeDist || saving) return;

    // The draft field keys (mouser_api_key, digikey_client_id, …) already match the
    // API; add the distributor type and map the label-based controls back to the
    // codes the API stores. company_id is derived from the auth token on the server.
    const payload = {
      // Credentials this screen does not render (e.g. Digikey's OAuth tokens)
      // are replayed first so the server-side api_keys rewrite preserves them;
      // anything the user actually edited overrides them below.
      ...(connected[activeDist.id]?.extra || {}),
      ...draft,
      type: activeDist.name,
      status: statusToCode(draft.status || "Enabled"),
    };

    // Environment selects are shown as Production/Sandbox but stored as 1/2 -
    // without this the label would be written back over the numeric code.
    activeDist.fields.forEach((f) => {
      if (f.type === "select" && f.options && draft[f.k] !== undefined) {
        payload[f.k] = envToCode(draft[f.k], f.options);
      }
    });

    setSaving(true);
    try {
      const res = await saveDistributorConfigAPI(payload);
      if (res?.status) {
        // A keyless distributor (TI) has no credential field to summarise, so
        // `primary` is undefined there - guard before reading it.
        const primary =
          activeDist.fields.find((f) => f.secret) || activeDist.fields.find((f) => !f.type);
        const raw = primary ? (draft[primary.k] || "").trim() : "";
        const masked = !primary
          ? "No API key required"
          : raw.length > 4
            ? `••••••••${raw.slice(-4)}`
            : "••••••••";
        setConnected((p) => ({
          ...p,
          // Keep `extra` so a second edit in the same session still replays the
          // unmanaged credentials.
          [activeDist.id]: { ...p[activeDist.id], values: { ...draft }, masked },
        }));
        setOpenId(null);
        setDraft({});
        iziToast.success({
          title: "Saved",
          message: `${activeDist.name} connected.`,
          position: "bottomCenter",
        });
      } else {
        iziToast.error({
          title: "Error",
          message: res?.message || "Couldn't save the distributor. Please try again.",
          position: "bottomCenter",
        });
      }
    } catch (e) {
      iziToast.error({
        title: "Error",
        message: "Something went wrong, please try again later.",
        position: "bottomCenter",
      });
    } finally {
      setSaving(false);
    }
  };
  const removeConnect = (id) => {
    setConnected((p) => {
      const n = { ...p };
      delete n[id];
      return n;
    });
    if (openId === id) setOpenId(null);
  };

  const hasOneKey = Object.keys(connected).length >= 1;
  const canFinish = billingValid && hasOneKey;

  return (
    <div className="sb-login-stage sb-dark" data-screen-label="Onboarding">
      <Glyph className="sb-glyph" />

      <div className="sb-shell" style={{ maxWidth: 560 }}>
        <div className="sb-brand-lockup">
          <Semi360Logo className="sb-brand-logo" />
        </div>

        <div className="sb-card">
          {/* Step rail */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <span
                className={`w-7 h-7 rounded-full inline-flex items-center justify-center text-[13px] font-bold ${stepCircle(
                  1,
                  step,
                )}`}
              >
                {step > 1 ? <Check className="w-3.5 h-3.5" /> : "1"}
              </span>
              <span className="text-[13px] font-semibold text-s360-fg1">Billing details</span>
            </div>
            <span className="flex-1 h-px bg-s360-stroke1" />
            <div className="flex items-center gap-2">
              <span
                className={`w-7 h-7 rounded-full inline-flex items-center justify-center text-[13px] font-bold ${stepCircle(
                  2,
                  step,
                )}`}
              >
                2
              </span>
              <span
                className={`text-[13px] font-semibold ${step === 2 ? "text-s360-fg1" : "text-s360-fg3"}`}
              >
                Connect a distributor
              </span>
            </div>
          </div>

          {step === 1 ? (
            <>
              <h1 style={{ fontSize: 24, lineHeight: "30px" }}>Add your billing details</h1>
              <p className="sb-lede">
                We use these to invoice your distributor orders. You can update them later in
                Settings.
              </p>

              <Field label="Billing email" error={billingErr("email")}>
                <div className={`sb-input-wrap ${billingErr("email") ? "sb-error" : ""}`}>
                  <span className="sb-lead">
                    <Mail className="w-[18px] h-[18px]" />
                  </span>
                  <input
                    type="email"
                    placeholder="billing@company.com"
                    value={billing.email}
                    onChange={(e) => setB("email", e.target.value)}
                    onBlur={() => markB("email")}
                  />
                </div>
              </Field>

              <Field label="Registered business name" error={billingErr("bizName")}>
                <div className={`sb-input-wrap ${billingErr("bizName") ? "sb-error" : ""}`}>
                  <span className="sb-lead">
                    <Building2 className="w-[18px] h-[18px]" />
                  </span>
                  <input
                    type="text"
                    placeholder="Acme Robotics Pvt. Ltd."
                    value={billing.bizName}
                    onChange={(e) => setB("bizName", e.target.value)}
                    onBlur={() => markB("bizName")}
                  />
                </div>
              </Field>

              <Field label="Billing address" error={billingErr("line1")}>
                <div className={`sb-input-wrap ${billingErr("line1") ? "sb-error" : ""}`}>
                  <span className="sb-lead">
                    <MapPin className="w-[18px] h-[18px]" />
                  </span>
                  <input
                    type="text"
                    placeholder="Street address"
                    value={billing.line1}
                    onChange={(e) => setB("line1", e.target.value)}
                    onBlur={() => markB("line1")}
                  />
                </div>
              </Field>

              <Field label="Apartment, suite, etc." optional>
                <div className="sb-input-wrap">
                  <input
                    type="text"
                    placeholder="Unit 4B"
                    value={billing.line2}
                    onChange={(e) => setB("line2", e.target.value)}
                  />
                </div>
              </Field>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                <Field label="City" error={billingErr("city")}>
                  <div className={`sb-input-wrap ${billingErr("city") ? "sb-error" : ""}`}>
                    <input
                      type="text"
                      placeholder="Bengaluru"
                      value={billing.city}
                      onChange={(e) => setB("city", e.target.value)}
                      onBlur={() => markB("city")}
                    />
                  </div>
                </Field>
                <Field label="State / Province" error={billingErr("state")}>
                  <div className={`sb-input-wrap ${billingErr("state") ? "sb-error" : ""}`}>
                    <input
                      type="text"
                      placeholder="Karnataka"
                      value={billing.state}
                      onChange={(e) => setB("state", e.target.value)}
                      onBlur={() => markB("state")}
                    />
                  </div>
                </Field>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                <Field label="Postal code" error={billingErr("postal")}>
                  <div className={`sb-input-wrap ${billingErr("postal") ? "sb-error" : ""}`}>
                    <input
                      type="text"
                      inputMode="numeric"
                      placeholder="560001"
                      value={billing.postal}
                      onChange={(e) => setB("postal", e.target.value)}
                      onBlur={() => markB("postal")}
                    />
                  </div>
                </Field>
                <Field label="Country">
                  <div className="sb-input-wrap">
                    <select
                      value={billing.country}
                      onChange={(e) => setB("country", e.target.value)}
                      style={{ all: "unset", flex: 1, height: "100%", padding: "0 8px", cursor: "pointer", color: "var(--sb-fg-1)", minWidth: 0, width: "100%" }}
                    >
                      {COUNTRIES.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                  </div>
                </Field>
              </div>

              <Field label="GSTIN / Tax ID" optional>
                <div className="sb-input-wrap">
                  <span className="sb-lead">
                    <FileText className="w-[18px] h-[18px]" />
                  </span>
                  <input
                    type="text"
                    placeholder="22AAAAA0000A1Z5"
                    maxLength={15}
                    value={billing.gstin}
                    onChange={(e) => setB("gstin", e.target.value.toUpperCase().slice(0, 15))}
                    style={{ fontFamily: "var(--sb-font-mono, monospace)", letterSpacing: ".02em" }}
                  />
                </div>
              </Field>

              <button
                type="button"
                className="sb-btn sb-btn-primary"
                onClick={continueToStep2}
                disabled={savingBilling}
              >
                {savingBilling ? "Saving…" : "Continue"} <ArrowRight className="w-4 h-4" />
              </button>
            </>
          ) : (
            <>
              <h1 style={{ fontSize: 24, lineHeight: "30px" }}>Connect a distributor</h1>
              <p className="sb-lede">
                Add at least one distributor API key so semi360 can benchmark live prices. You can
                add the rest anytime in Settings.
              </p>

              <div className="flex flex-col gap-2">
                {DISTRIBUTORS.map((d) => {
                  const isConn = !!connected[d.id];
                  const isOpen = openId === d.id;
                  return (
                    <div key={d.id} className="border border-s360-stroke1 rounded-md overflow-hidden">
                      <div
                        className={`flex items-center gap-3 px-3 py-2.5 ${isConn ? "bg-s360-bg2" : "bg-white"}`}
                      >
                        <span
                          className="w-8 h-8 rounded-md inline-flex items-center justify-center text-[11px] font-bold text-white shrink-0"
                          style={{ background: d.color }}
                        >
                          {d.short}
                        </span>
                        <div className="flex flex-col min-w-0 flex-1">
                          <span className="text-sm font-semibold text-s360-fg1">{d.name}</span>
                          <span className="text-xs text-s360-fg3 inline-flex items-center gap-1">
                            {isConn ? (
                              <>
                                <Check className="w-3 h-3 text-s360-teal" /> Connected ·{" "}
                                {connected[d.id].masked}
                              </>
                            ) : (
                              d.blurb
                            )}
                          </span>
                        </div>
                        {isConn ? (
                          <div className="inline-flex gap-1.5">
                            <button
                              type="button"
                              className="h-8 px-2.5 rounded-md text-[13px] font-semibold text-s360-fg1 hover:bg-s360-bg2"
                              onClick={() => openConnect(d.id)}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              className="h-8 px-2.5 rounded-md text-[13px] font-semibold text-s360-danger hover:bg-s360-danger-bg"
                              onClick={() => removeConnect(d.id)}
                            >
                              Remove
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-white border border-s360-stroke1 text-[13px] font-semibold text-s360-fg1 hover:bg-s360-bg2"
                            onClick={() => openConnect(d.id)}
                          >
                            <Plus className="w-3.5 h-3.5" /> Add key
                          </button>
                        )}
                      </div>

                      {isOpen && (
                        <div className="flex flex-col gap-3 p-3 border-t border-s360-stroke1 bg-s360-stripe">
                          {d.fields.map((f) => {
                            if (f.type === "toggle") {
                              const on = (draft[f.k] || f.options[0]) === f.options[0];
                              return (
                                <Field key={f.k} label={f.label}>
                                  <div className="flex items-center gap-2.5 h-10">
                                    <button
                                      type="button"
                                      role="switch"
                                      aria-checked={on}
                                      onClick={() => editDraft(f.k, on ? f.options[1] : f.options[0])}
                                      className={`w-10 h-5 rounded-full relative transition-colors ${on ? "bg-s360-violet" : "bg-s360-bg3"}`}
                                    >
                                      <span
                                        className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${on ? "left-[22px]" : "left-0.5"}`}
                                      />
                                    </button>
                                    <span
                                      className="text-[13px] font-semibold"
                                      style={{ color: on ? "var(--sb-success)" : "var(--sb-fg-3)" }}
                                    >
                                      {on ? f.options[0] : f.options[1]}
                                    </span>
                                  </div>
                                </Field>
                              );
                            }
                            if (f.type === "select") {
                              return (
                                <Field key={f.k} label={f.label}>
                                  <div className="sb-input-wrap">
                                    <select
                                      value={draft[f.k] || f.options[0]}
                                      onChange={(e) => editDraft(f.k, e.target.value)}
                                      style={{ all: "unset", flex: 1, height: "100%", padding: "0 8px", cursor: "pointer", color: "var(--sb-fg-1)", minWidth: 0, width: "100%" }}
                                    >
                                      {f.options.map((o) => (
                                        <option key={o} value={o}>
                                          {o}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                </Field>
                              );
                            }
                            const err = draftTouched ? fieldError(f) : "";
                            return (
                              <Field key={f.k} label={f.label} optional={f.optional} error={err}>
                                <div className={`sb-input-wrap ${err ? "sb-error" : ""}`}>
                                  <span className="sb-lead">
                                    {f.email ? (
                                      <Mail className="w-[18px] h-[18px]" />
                                    ) : (
                                      <KeyRound className="w-[18px] h-[18px]" />
                                    )}
                                  </span>
                                  <input
                                    type={f.email ? "email" : f.secret ? "password" : "text"}
                                    placeholder={f.ph}
                                    value={draft[f.k] || ""}
                                    onChange={(e) => editDraft(f.k, e.target.value)}
                                    autoComplete="off"
                                  />
                                </div>
                              </Field>
                            );
                          })}

                          {testStatus === "ok" && (
                            <span className="sb-error-text" style={{ color: "var(--sb-success)" }}>
                              <Check className="w-3.5 h-3.5" />{" "}
                              {testMessage || "Connection verified — keys look good."}
                            </span>
                          )}
                          {testStatus === "fail" && (
                            <span className="sb-error-text">
                              <AlertCircle className="w-3.5 h-3.5" />{" "}
                              {testMessage || "Couldn't connect. Check your keys and try again."}
                            </span>
                          )}

                          <div className="flex items-center justify-between gap-2">
                            <button
                              type="button"
                              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-[13px] font-semibold text-s360-violet hover:bg-s360-violet-50 disabled:opacity-60"
                              onClick={testConnection}
                              disabled={testStatus === "testing"}
                            >
                              <Activity className="w-3.5 h-3.5" />
                              {testStatus === "testing" ? "Testing…" : "Test connection"}
                            </button>
                            <div className="inline-flex gap-2">
                              <button
                                type="button"
                                className="h-9 px-3 rounded-md text-[13px] font-semibold text-s360-fg1 hover:bg-s360-bg2"
                                onClick={() => {
                                  setOpenId(null);
                                  setDraft({});
                                  setTestStatus("idle");
                                  setTestMessage("");
                                }}
                              >
                                Cancel
                              </button>
                              <button
                                type="button"
                                className="h-9 px-4 rounded-md bg-s360-violet text-white text-[13px] font-semibold hover:bg-s360-violet-600 disabled:opacity-60"
                                onClick={saveConnect}
                                disabled={saving}
                              >
                                {saving ? "Saving…" : "Save key"}
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {!hasOneKey && (
                <p className="text-xs text-s360-fg3 inline-flex items-center gap-1.5 m-0">
                  <AlertCircle className="w-3.5 h-3.5" /> Connect at least one distributor to continue.
                </p>
              )}

              <div className="flex gap-2.5">
                <button
                  type="button"
                  className="inline-flex items-center gap-1.5 h-11 px-4 rounded-md bg-white border border-s360-stroke1 text-sm font-semibold text-s360-fg1 hover:bg-s360-bg2"
                  onClick={() => setStep(1)}
                >
                  <ArrowLeft className="w-4 h-4" /> Back
                </button>
                <button
                  type="button"
                  className="sb-btn sb-btn-primary flex-1"
                  disabled={!canFinish}
                  onClick={() => {
                    try {
                      sessionStorage.removeItem("in_onboarding");
                    } catch (e) {
                      /* ignore */
                    }
                    navigate("/get-started");
                  }}
                >
                  Enter dashboard <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </>
          )}
        </div>

        <div className="sb-legal">
          Signed in as {email} ·{" "}
          <button
            type="button"
            className="sb-link"
            onClick={() => {
              try {
                sessionStorage.removeItem("in_onboarding");
              } catch (e) {
                /* ignore */
              }
              navigate("/");
            }}
          >
            Log out
          </button>
        </div>
      </div>
    </div>
  );
};

export default OnboardingPage;
